import { ChangeDetectionStrategy, Component, ViewChild, Injector } from '@angular/core';
import { MatTabGroup } from '@angular/material/tabs';
import { ComponentStore } from '@ngrx/component-store';
import { Store } from '@ngrx/store';
import { BaseApiTemplateService } from 'blg-akaun-ts-lib';
import { gridOptionsClientSide } from '../../../../../../../../../listing.utils';
import { PermSetLine } from '../../../../../../../../../models/perm-set.model';
import { PermissionFacade } from '../../../../../../../../../modules/permission/facades/permission.facade';
import { PermSetActions } from '../../../../../../../../../modules/permission/perm-set-controller/actions';
import { PermSetSelectors } from '../../../../../../../../../modules/permission/perm-set-controller/selectors';
import { PermissionStates } from '../../../../../../../permission-controller/root';
import { ViewColumnComponent } from '../../../../../../../../../view-column.component';
import { AppConfig } from '../../../../../../../../../visa';
import { map } from 'rxjs/operators';
import { SubSink } from 'subsink2';
import { TranslateService } from "@ngx-translate/core";

interface LocalState {
  deactivateAdd: boolean,
  deactivateList: boolean,
  deactivateReturn: boolean,
  selectedIndex: number
}

@Component({
  selector: 'app-perm-set-edit-targets',
  templateUrl: './perm-set-edit-targets.component.html',
  styleUrls: ['./perm-set-edit-targets.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [ComponentStore]
})
export class PermSetEditTargetsComponent extends ViewColumnComponent {

  @ViewChild(MatTabGroup, {static: true}) matTab: MatTabGroup;

  protected readonly index = 5;
  private localState: LocalState;

  readonly localState$ = this.viewColFacade.selectLocalState(this.index);
  readonly deactivateReturn$ = this.componentStore.select(state => state.localState.deactivateReturn);
  readonly selectedIndex$ = this.componentStore.select(state => (state.localState.selectedIndex));

  prevIndex: number;
  private prevLocalState: any;

  apiVisa = AppConfig.apiVisa;

  // subSink is to ensure all subscribe calls are unsubscribed to once the component is destroyed
  private subSink = new SubSink;
  searchValue: any;

  // initialise ag grid
  gridApi;
  gridOptions = gridOptionsClientSide;
  columnsDefs;
  columnsDefs$;
  rowSelection = 'single';
  rowData$ = [];

  selectedPermSetLine: PermSetLine;
  defaultTargetViewName: string;
  defaultTargetTable: string;
  defaultViewField: string;
  defaultService: BaseApiTemplateService<any>;

  // keep track of save button being disabled
  saveDisabled: boolean = true;

  //translation variable
  title: string;
  back_button: string;
  save_button: string;
  search_text: string;

  constructor(
    private viewColFacade: PermissionFacade,
    private store: Store<PermissionStates>,
    private componentStore: ComponentStore<{localState: LocalState}>,
    private injector: Injector
  ) {
    super();
    const translateService = this.injector.get(TranslateService, null);
    if (translateService) {
      translateService.get('labels').subscribe(res => {
        this.title = res.add_target;
        this.back_button = res.back_button;
        this.save_button = res.save_button;
        this.search_text = res.search;
      });
    } else {
      // Fallback for applets that don't implement translation
      this.title = 'Add Target';
      this.back_button = 'Back';
      this.save_button = 'SAVE';
      this.search_text = 'Search';
    }
  }

  /**
   * Lifecycle method: Called when the component is first initialised.
   */
  ngOnInit(): void {
    this.subSink.sink = this.localState$.subscribe(a => {
      this.localState = a;
      this.componentStore.setState({localState: a});
    });
    this.subSink.sink = this.viewColFacade.prevIndex$.subscribe(resolve => this.prevIndex = resolve);
    this.subSink.sink = this.viewColFacade.prevLocalState$().subscribe(resolve => this.prevLocalState = resolve);

    // this.columnsDefs = [
    //   {
    //     headerName: 'Code',
    //     field: `code`,
    //     cellStyle: {textAlign: 'left'},
    //     checkboxSelection: true,
    //   },
    //   {
    //     headerName: 'Name',
    //     field: `name`,
    //     cellStyle: {textAlign: 'left'}
    //   }
    // ];
    this.columnsDefs$ = this.store.select(PermSetSelectors.selectPermSetLine)
    .pipe(
      map(line => this.targetServices.getColumnsDefs(line.default_target_view_name))
    );
  }

  /**
   * This function is called when AG grid is first initialised and initally
   * sets the datasource of the grid.
   *
   * @param params : grid parameters
   */
   onGridReady(params) {
    this.gridApi = params.api;
    this.gridApi.closeToolPanel();

    const selectLine$ = this.store.select(PermSetSelectors.selectPermSetLine);

    this.subSink.sink = selectLine$
    .subscribe(line => {
      this.selectedPermSetLine = line;
      this.defaultTargetViewName = line.default_target_view_name;
      this.defaultTargetTable = line.app_perm_set_line.target_hdr_table.toString();

      this.defaultService = this.targetServices.getService(this.defaultTargetViewName);
      this.defaultViewField = this.targetServices.getViewField(this.defaultTargetViewName);
      const colDefs = this.targetServices.getColumnsDefs(this.defaultTargetViewName);

      this.subSink.sink = this.defaultService.get(this.apiVisa)
      .subscribe(x => {
        this.rowData$ = [];

        // choice for all
        // this.rowData$.push({
        //   guid: null,
        //   code: 'ALL',
        //   name: 'ALL'
        // });

        // create a row just for all
        const allRow = colDefs.reduce((acc, x) => {
          acc[x['field']] = 'ALL';
          return acc
        }, {
          guid: null
        })
        this.rowData$.push(allRow);

        x.data.forEach(data => {
          this.rowData$.push(data[this.defaultTargetTable]);
        });

        this.gridApi.setRowData(this.rowData$);

        // pre-select row node if a default company has already been selected
        if (this.selectedPermSetLine.app_perm_set_line.target_hdr_guid != null) {
          this.gridApi.forEachNode(node => {
            if (node.data.guid != null && node.data.guid.toString() == this.selectedPermSetLine.app_perm_set_line.target_hdr_guid.toString()) {
              node.setSelected(true);
            }
          })
        }

      })
    });

  }

  /**
   * This functions is called when the user types something in the search bar.
   * Will cause the AG grid to filter its data accordingly.
   *
   */
   onSearch() {
    this.gridApi.setQuickFilter(this.searchValue);
  }

  /**
   * This function is called when the user clears the search bar. Will cause
   * the AG grid to show all the row data accordingly.
   *
   */
   reset() {
    this.searchValue = '';
  }

  /**
   * Disable save button if no nodes have been selected and enable if at least
   * one node has been selected.
   */
  onSelectionChanged() {
    const selectedNodes = this.gridApi.getSelectedNodes();
    this.saveDisabled = selectedNodes.length > 0 ? false : true;
  }

  onRowDataChanged() {
    // pre-select row node if a default target has already been selected
    if (this.selectedPermSetLine && this.selectedPermSetLine.app_perm_set_line.target_hdr_guid != null) {
      this.gridApi.forEachNode(node => {
        if (node.data.guid != null && node.data.guid.toString() == this.selectedPermSetLine.app_perm_set_line.target_hdr_guid.toString()) {
          node.setSelected(true);
        }
      })
    }
  }

  /**
   * Update the permission set line with the default target header.
   */
  onSubmit() {
    const entity = this.gridApi.getSelectedNodes()[0].data;

    // set target header guid and default target name
    this.selectedPermSetLine.app_perm_set_line.target_hdr_guid = entity.guid;
    this.selectedPermSetLine.default_target_hdr_name = entity[this.defaultViewField];

    // save updated perm set line in the store
    this.store.dispatch(PermSetActions.selectPermSetLine({permSetLine: this.selectedPermSetLine}));

    this.onReturn();
  }

  /**
   * Return to the previous drawer/view column.
   */
   onReturn() {
    this.viewColFacade.updateInstance(this.prevIndex, {
      ...this.prevLocalState,
      deactivateReturn: false
    });
    this.viewColFacade.onPrev(this.prevIndex);
  }

  /**
   * Lifecycle method: Called when component is destroyed.
   */
   ngOnDestroy() {
    this.subSink.unsubscribe();
  }

}
